/**
 * @file Shared onboarding strings — imported by both web and mobile.
 *
 * All labels, tooltips, helper text, and validation messages for the
 * onboarding wizard live here so changes propagate to both platforms.
 *
 * Usage:
 *   import { ONBOARDING } from '../../shared/onboarding-strings';   // web
 *   import { ONBOARDING } from '../shared/onboarding-strings';      // mobile
 */

export const ONBOARDING = {
  // Top-level
  PAGE_TITLE: 'Configura tu perfil',
  PAGE_SUBTITLE: 'Solo toma unos minutos.',

  STEPS: ['Perfil público', 'Configuración', 'Servicios', 'Ubicación', 'Horario', '¡Listo!'],

  // Step 0 — Perfil público
  STEP0_HEADING: '¿Cómo quieres presentarte?',
  STEP0_SUBHEADING: 'Esta información aparecerá en tu perfil público para que tus pacientes te encuentren.',
  TITLE_LABEL: 'Título profesional',
  TITLE_NONE: 'Sin título',
  SPECIALTY_LABEL: 'Especialidad *',
  SPECIALTY_PLACEHOLDER: 'Selecciona una especialidad',
  SPECIALTY_OTHER_LABEL: 'Especifica tu especialidad *',
  SPECIALTY_OTHER_PLACEHOLDER: 'Escribe tu especialidad',
  CEDULA_LABEL: 'Cédula profesional *',
  CEDULA_PLACEHOLDER: 'Número de cédula',
  CEDULA_HELPER: 'Si tienes cédulas de otras especialidades, puedes agregarlas después en Mi Perfil.',
  SLUG_SECTION_TITLE: 'Tu enlace de perfil público',
  SLUG_HELPER: 'Este es el link que compartirás con tus pacientes para que agenden citas.',
  SLUG_LABEL: 'URL de tu perfil público',
  SLUG_PLACEHOLDER: 'tu-nombre',

  // Step 0 — validation
  STEP0_SPECIALTY_REQUIRED: 'Selecciona tu especialidad',
  STEP0_SPECIALTY_OTHER_REQUIRED: 'Especifica tu especialidad',
  STEP0_CEDULA_REQUIRED: 'La cédula profesional es obligatoria',
  STEP0_SLUG_REQUIRED: 'El enlace de perfil no puede estar vacío',
  STEP0_SLUG_TAKEN: 'Este enlace ya está en uso',

  // Step 1 — Configuración de sesión
  STEP1_HEADING: '¿Cómo son tus consultas?',
  STEP1_SUBHEADING: 'Define dónde y cuánto duran tus consultas.',
  COUNTRY_LABEL: '¿Dónde ofreces tus consultas? *',
  COUNTRY_HELPER: 'Este es el país donde ejerces. No podrás cambiarlo después.',
  SESSION_DURATION_LABEL: 'Duración de la sesión (min) *',
  SESSION_DURATION_PLACEHOLDER: '60',
  BUFFER_LABEL: 'Tiempo entre citas (min)',
  BUFFER_PLACEHOLDER: '10',
  BUFFER_HELPER: 'Tiempo de descanso entre una cita y la siguiente',
  CURRENCY_LABEL: 'Moneda',
  CURRENCY_HELPER: 'Se usa para mostrar los precios de tus servicios a los pacientes',

  // Step 1 — Public contact info
  PUBLIC_CONTACT_HEADING: 'Datos de contacto públicos',
  PUBLIC_CONTACT_HELPER: 'Estos datos se mostrarán en tu perfil público a pacientes registrados. Son opcionales.',
  PUBLIC_PHONE_LABEL: 'Teléfono para agendar consultas',
  PUBLIC_PHONE_PLACEHOLDER: '+52 55 1234 5678',
  PUBLIC_EMAIL_LABEL: 'Correo público',
  PUBLIC_EMAIL_PLACEHOLDER: 'consultorio@ejemplo.com',

  // Step 1 — validation
  STEP1_DURATION_MIN: 'La duración mínima es 15 minutos',

  // Step 2 — Servicios
  STEP2_HEADING: '¿Qué servicios ofreces?',
  STEP2_SUBHEADING: 'Agrega al menos un servicio con su precio. Por ejemplo: "Consulta general", "Consulta de seguimiento".',
  SERVICE_NAME_LABEL: 'Nombre del servicio',
  SERVICE_NAME_PLACEHOLDER: 'Ej. Consulta general',
  SERVICE_ADD: 'Agregar',
  SERVICE_ADD_MOBILE: 'Agregar servicio',

  // Step 2 — validation
  STEP2_MIN_SERVICES: 'Agrega al menos un servicio',
  STEP2_NAME_REQUIRED: 'El nombre del servicio es obligatorio',
  STEP2_PRICE_REQUIRED: 'El precio debe ser mayor a 0',

  // Step 3 — Ubicación
  STEP3_HEADING: '¿Dónde atiendes?',
  STEP3_SUBHEADING: 'Agrega al menos una ubicación. Puedes tener consultorios presenciales y también ofrecer teleconsulta.',
  LOC_VIRTUAL_BUTTON: 'Agregar teleconsulta / videollamada',
  LOC_VIRTUAL_NAME: 'Teleconsulta',
  LOC_PHYSICAL_TITLE: 'Agregar consultorio presencial',
  LOC_NAME_LABEL: 'Nombre (opcional)',
  LOC_NAME_PLACEHOLDER: 'Ej: Consultorio principal',
  LOC_ADDRESS_LABEL: 'Calle y número *',
  LOC_ADDRESS_PLACEHOLDER: 'Ej: Av. Hidalgo 123',
  LOC_COLONIA_LABEL: 'Colonia',
  LOC_COLONIA_PLACEHOLDER: 'Ej: Centro',
  LOC_CITY_LABEL: 'Ciudad',
  LOC_STATE_LABEL: 'Estado / Provincia',
  LOC_STATE_PLACEHOLDER: 'Seleccionar estado',
  LOC_ADD_BUTTON: 'Agregar consultorio',

  // Step 3 — location limit messages
  LOC_LIMIT_REACHED: "Has alcanzado el límite de ubicaciones presenciales de tu plan.",
  LOC_LIMIT_TRY_TRIAL: 'Puedes activar la Prueba Pro gratis desde "Mi Plan" después de completar tu perfil para agregar otra ubicación.',
  LOC_LIMIT_UPGRADE_PRO: 'Actualiza a Pro desde "Mi Plan" después de completar tu perfil para agregar más ubicaciones.',

  // Step 3 — validation
  STEP3_MIN_LOCATIONS: 'Agrega al menos una ubicación o teleconsulta',
  STEP3_ADDRESS_REQUIRED: 'La dirección es obligatoria',

  // Step 4 — Horario
  STEP4_HEADING: '¿Cuándo atiendes?',
  STEP4_SUBHEADING: 'Configura tu horario semanal. Podrás agregar excepciones después.',
  SCHEDULE_DAY_LABEL: 'Día de la semana',
  SCHEDULE_START_LABEL: 'Hora inicio',
  SCHEDULE_END_LABEL: 'Hora fin',
  SCHEDULE_LOCATION_LABEL: 'Ubicación',
  SCHEDULE_LOCATION_HELPER: 'Selecciona el consultorio o teleconsulta para este bloque',
  SCHEDULE_ADD_BUTTON: 'Agregar horario',
  SCHEDULE_TELECONSULTA_INFO: '¿Cuándo puede agendarse teleconsulta? Por defecto, los pacientes pueden reservar videollamada durante cualquier bloque de horario, incluso los asignados a un consultorio presencial. Si prefieres que las teleconsultas solo se agenden en horarios específicos, puedes activar esa opción en la configuración de tus horarios.',

  // Step 4 — validation
  STEP4_MIN_SCHEDULES: 'Agrega al menos un bloque de horario',
  STEP4_TIME_ORDER: 'La hora de inicio debe ser antes que la hora de fin',
  STEP4_LOCATION_REQUIRED: 'Selecciona una ubicación para este bloque de horario',

  // Step 5 — ¡Listo!
  STEP5_HEADING: '¡Tu perfil está listo!',
  STEP5_SUBHEADING: 'Empieza a compartirlo para recibir tus primeras citas',
  STEP5_SUBHEADING_MOBILE: 'Tus pacientes ya pueden encontrarte y agendar citas desde tu perfil público.',
  STEP5_LINK_TITLE: 'Tu enlace de perfil',
  STEP5_LINK_LABEL: 'Tu enlace público:',
  STEP5_COPY_SUCCESS: '¡Enlace copiado!',
  STEP5_INSTAGRAM_COPY: '¡Enlace copiado! Pégalo en tu bio o historia de Instagram.',
  STEP5_QR_TITLE: 'Código QR',
  STEP5_QR_DESC: 'Imprime o comparte este código para que tus pacientes accedan a tu perfil con solo escanearlo.',
  STEP5_SHARE_MESSAGE: 'Agenda tu cita conmigo en Consultto:',
  STEP5_SHARE_BUTTON: 'Compartir mi perfil',
  STEP5_FINISH: 'Ir a mi dashboard',

  // Common
  NEXT: 'Siguiente',
  BACK: 'Atrás',
  SAVING: 'Guardando...',
  ADDING: 'Agregando...',
  FINISHING: 'Finalizando...',
  SAVE_ERROR: 'Error al guardar. Intenta de nuevo.',
};
